import { useState, useCallback, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useFirmaContext } from '@/contexts/FirmaContext'
import type { Ansprechpartner, AccessLevel, FirmaRole } from '@/types'
import { Card, CardHeader, CardBody } from '@/components/Card'
import EmptyState from '@/components/EmptyState'
import { Star, Plus, Trash2, X, Save, AlertTriangle, Settings, CheckCircle2, Circle, Loader2 } from 'lucide-react'
import FirmaAdminDrawer from '@/components/FirmaAdminDrawer'

// ── Funktion Optionen ──────────────────────────────────────

const FUNKTION_OPTIONS = [
  { value: 100000061, label: 'Wichtige Kontaktperson' },
  { value: 100000024, label: 'Geschäftsführer/-in' },
  { value: 100000042, label: 'Personalreferent/-entwickler/-in' },
  { value: 100000010, label: 'Personalabteilung' },
  { value: 100000040, label: 'Rechnungsempfänger/-in' },
  { value: 100000041, label: 'Empfänger Imma-Unterlagen' },
]

// ── Neuen AP Formular ──────────────────────────────────────

interface NewApForm {
  fullname: string
  email: string
  telefon: string
  mobil: string
  position: string
  funktion: number
}

const EMPTY_FORM: NewApForm = {
  fullname: '',
  email: '',
  telefon: '',
  mobil: '',
  position: '',
  funktion: 100000061,
}

// ── Props ──────────────────────────────────────────────────

interface Props {
  ansprechpartner: Ansprechpartner[]
  accountId: string
}

export default function FirmaAnsprechpartner({ ansprechpartner, accountId }: Props) {
  const { isFirmaAdmin } = useFirmaContext()
  const queryClient = useQueryClient()

  // Create-Dialog
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<NewApForm>(EMPTY_FORM)
  const nameRef = useRef<HTMLInputElement>(null)

  // Delete-Confirm
  const [deleteTarget, setDeleteTarget] = useState<{ apId: string; name: string } | null>(null)

  // Drawer state for permission management
  const [drawerOpen, setDrawerOpen] = useState<{ apUserId: number; name: string } | null>(null)
  const [drawerData, setDrawerData] = useState<{ role: FirmaRole | null; perms: Record<string, AccessLevel> } | null>(null)

  // Lade Liste aller User dieser Firma — für alle sichtbar, da daraus
  // pro Ansprechpartner abgeleitet wird, ob er bereits einen MyFOM-Account hat.
  const { data: firmaUsers, isLoading: firmaUsersLoading } = useQuery({
    queryKey: ['firmaUsers', accountId],
    queryFn: () => api.getFirmaUsers(accountId),
    enabled: !!accountId,
  })

  const userEmails = new Set(
    (firmaUsers?.users ?? []).map(u => (u.Email ?? '').toLowerCase()).filter(Boolean)
  )

  // ── Mutations ────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createAnsprechpartner(accountId, data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['firma', accountId] })
      setShowCreate(false)
      setForm(EMPTY_FORM)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (apId: string) => api.deleteAnsprechpartner(accountId, apId),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['firma', accountId] })
      setDeleteTarget(null)
    },
  })

  const handleCreate = useCallback(() => {
    if (!form.fullname.trim()) {
      nameRef.current?.focus()
      return
    }
    createMutation.mutate({
      fullname: form.fullname,
      email: form.email || undefined,
      telefon: form.telefon || undefined,
      mobil: form.mobil || undefined,
      position: form.position || undefined,
      funktion: form.funktion,
    })
  }, [form, createMutation])

  const handleDelete = useCallback(() => {
    if (deleteTarget?.apId) {
      deleteMutation.mutate(deleteTarget.apId)
    }
  }, [deleteTarget, deleteMutation])

  // Sortierung: Hauptansprechpartner zuerst, dann alphabetisch
  const sorted = [...ansprechpartner].sort((a, b) => {
    if (a.ist_hauptansprechpartner && !b.ist_hauptansprechpartner) return -1
    if (!a.ist_hauptansprechpartner && b.ist_hauptansprechpartner) return 1
    return (a.fullname || '').localeCompare(b.fullname || '')
  })

  return (
    <div className="space-y-4">
      {/* ── Fehler-Meldungen ──────────────────────────── */}
      {createMutation.isError && (
        <div className="px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[0.82rem]">
          Fehler: {createMutation.error instanceof Error ? createMutation.error.message : 'Anlegen fehlgeschlagen'}
        </div>
      )}
      {deleteMutation.isError && (
        <div className="px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[0.82rem]">
          Fehler: {deleteMutation.error instanceof Error ? deleteMutation.error.message : 'Löschen fehlgeschlagen'}
        </div>
      )}

      {/* ── Delete-Bestätigung ────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-500 flex items-center justify-center flex-none">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h3 className="text-[0.95rem] font-semibold text-gray-900">Ansprechpartner entfernen</h3>
                <p className="text-[0.8rem] text-gray-500 mt-0.5">
                  Soll <strong>{deleteTarget.name}</strong> wirklich entfernt werden?
                </p>
              </div>
            </div>
            <p className="text-[0.78rem] text-gray-500 mb-5">
              Die Zuordnung wird im CRM deaktiviert. Der Kontakt-Datensatz bleibt erhalten.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-[0.8rem] font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-[0.8rem] font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Wird entfernt…' : 'Entfernen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tabelle ───────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <span>Ansprechpartner ({ansprechpartner.length})</span>
            {isFirmaAdmin && (
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 text-[0.72rem] font-medium text-fom-600 hover:text-fom-700 transition-colors"
              >
                <Plus size={14} /> Neu anlegen
              </button>
            )}
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {sorted.length === 0 && !showCreate ? (
            <div className="p-5">
              <EmptyState message="Keine Ansprechpartner verknüpft" icon />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Name', 'MyFOM-Account', 'E-Mail', 'Telefon', 'Mobil', ...(isFirmaAdmin ? [''] : [])].map((h) => (
                      <th
                        key={h || '_actions'}
                        className="text-left px-4 py-2.5 text-[0.66rem] text-gray-500 border-b border-gray-200 uppercase tracking-widest font-semibold bg-gray-50"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((ap, i) => (
                    <tr
                      key={ap.ap_id || i}
                      className={`transition-colors hover:bg-gray-50 ${
                        ap.ist_hauptansprechpartner ? 'bg-fom-50/30' : ''
                      }`}
                    >
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem]">
                        <div className="flex items-center gap-2">
                          {ap.ist_hauptansprechpartner && (
                            <Star size={14} className="text-amber-500 fill-amber-400 flex-none" />
                          )}
                          <span className="font-semibold">{ap.fullname}</span>
                          {ap.ist_hauptansprechpartner && (
                            <span className="text-[0.62rem] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider whitespace-nowrap">
                              Hauptkontakt
                            </span>
                          )}
                        </div>
                        {ap.position && (
                          <div className="text-[0.72rem] text-gray-500 mt-0.5">{ap.position}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem]">
                        {firmaUsersLoading ? (
                          <span className="inline-flex items-center gap-1.5 text-[0.78rem] text-gray-300">
                            <Loader2 size={14} className="animate-spin" />
                            <span>…</span>
                          </span>
                        ) : ap.email && userEmails.has(ap.email.toLowerCase()) ? (
                          <span className="inline-flex items-center gap-1.5 text-[0.78rem] text-emerald-700">
                            <CheckCircle2 size={14} className="text-emerald-500" />
                            <span className="font-medium">Aktiv</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[0.78rem] text-gray-400">
                            <Circle size={14} />
                            <span>Noch keiner</span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem]">
                        {ap.email ? (
                          <a
                            href={`mailto:${ap.email}`}
                            className="text-fom-700 hover:text-fom-500 hover:underline font-medium"
                          >
                            {ap.email}
                          </a>
                        ) : (
                          '–'
                        )}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem]">
                        {ap.telefon || '–'}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem]">
                        {ap.mobil || '–'}
                      </td>
                      {isFirmaAdmin && (
                        <td className="px-4 py-2.5 border-b border-gray-100 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {ap.email && (() => {
                              const userMatch = firmaUsers?.users?.find(
                                u => u.Email.toLowerCase() === ap.email!.toLowerCase()
                              )
                              return userMatch ? (
                                <button
                                  onClick={() => {
                                    setDrawerOpen({ apUserId: userMatch.Id, name: ap.fullname || ap.name })
                                    setDrawerData({
                                      role: userMatch.FirmaRole ?? null,
                                      perms: {},
                                    })
                                  }}
                                  title="Berechtigungen verwalten"
                                  className="p-1 text-gray-400 hover:text-fom-500 bg-transparent border-none cursor-pointer transition-colors rounded hover:bg-fom-50"
                                >
                                  <Settings size={14} />
                                </button>
                              ) : null
                            })()}
                            {ap.ap_id && !ap.ist_hauptansprechpartner && (
                              <button
                                onClick={() => setDeleteTarget({ apId: ap.ap_id!, name: ap.fullname })}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-red-50"
                                title="Entfernen"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Permissions Drawer ───────────────────────── */}
      {drawerOpen && drawerData && (
        <FirmaAdminDrawer
          isOpen={!!drawerOpen}
          onClose={() => { setDrawerOpen(null); setDrawerData(null) }}
          apUserId={drawerOpen.apUserId}
          apName={drawerOpen.name}
          accountId={accountId}
          currentRole={drawerData.role}
          currentPermissions={drawerData.perms}
        />
      )}

      {/* ── Neuen AP anlegen Modal ───────────────────── */}
      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowCreate(false); setForm(EMPTY_FORM); createMutation.reset() } }}
        >
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              <span>Neuen Ansprechpartner anlegen</span>
              <button
                onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); createMutation.reset() }}
                className="flex items-center gap-1 text-[0.72rem] font-medium text-gray-500 hover:text-gray-700 transition-colors bg-transparent border-none cursor-pointer"
              >
                <X size={13} /> Abbrechen
              </button>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              {/* Name (Pflicht) */}
              <div>
                <label className="block text-[0.72rem] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Name <span className="text-red-400">*</span>
                </label>
                <input
                  ref={nameRef}
                  type="text"
                  value={form.fullname}
                  onChange={(e) => setForm(prev => ({ ...prev, fullname: e.target.value }))}
                  placeholder="Vorname Nachname"
                  className="w-full px-3 py-2 text-[0.82rem] rounded-lg border border-gray-200 bg-white/80 focus:border-fom-300 focus:ring-2 focus:ring-fom-100 outline-none transition-colors"
                />
              </div>

              {/* E-Mail */}
              <div>
                <label className="block text-[0.72rem] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  E-Mail
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="name@firma.de"
                  className="w-full px-3 py-2 text-[0.82rem] rounded-lg border border-gray-200 bg-white/80 focus:border-fom-300 focus:ring-2 focus:ring-fom-100 outline-none transition-colors"
                />
              </div>

              {/* Telefon */}
              <div>
                <label className="block text-[0.72rem] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={form.telefon}
                  onChange={(e) => setForm(prev => ({ ...prev, telefon: e.target.value }))}
                  className="w-full px-3 py-2 text-[0.82rem] rounded-lg border border-gray-200 bg-white/80 focus:border-fom-300 focus:ring-2 focus:ring-fom-100 outline-none transition-colors"
                />
              </div>

              {/* Mobil */}
              <div>
                <label className="block text-[0.72rem] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Mobil
                </label>
                <input
                  type="tel"
                  value={form.mobil}
                  onChange={(e) => setForm(prev => ({ ...prev, mobil: e.target.value }))}
                  className="w-full px-3 py-2 text-[0.82rem] rounded-lg border border-gray-200 bg-white/80 focus:border-fom-300 focus:ring-2 focus:ring-fom-100 outline-none transition-colors"
                />
              </div>

              {/* Position */}
              <div>
                <label className="block text-[0.72rem] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Position / Jobtitel
                </label>
                <input
                  type="text"
                  value={form.position}
                  onChange={(e) => setForm(prev => ({ ...prev, position: e.target.value }))}
                  className="w-full px-3 py-2 text-[0.82rem] rounded-lg border border-gray-200 bg-white/80 focus:border-fom-300 focus:ring-2 focus:ring-fom-100 outline-none transition-colors"
                />
              </div>

              {/* Funktion */}
              <div>
                <label className="block text-[0.72rem] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Funktion
                </label>
                <select
                  value={form.funktion}
                  onChange={(e) => setForm(prev => ({ ...prev, funktion: Number(e.target.value) }))}
                  className="w-full px-3 py-2 text-[0.82rem] rounded-lg border border-gray-200 bg-white/80 focus:border-fom-300 focus:ring-2 focus:ring-fom-100 outline-none transition-colors"
                >
                  {FUNKTION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); createMutation.reset() }}
                className="px-4 py-2 text-[0.8rem] font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || !form.fullname.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-[0.8rem] font-semibold text-white bg-fom-600 hover:bg-fom-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <Save size={14} />
                {createMutation.isPending ? 'Wird angelegt…' : 'Anlegen'}
              </button>
            </div>
          </CardBody>
        </Card>
        </div>
      )}
    </div>
  )
}
